import { useQuizBrowserBack } from './quizHistoryGuard';
import { SETTINGS_ROUTES } from "../../settingProfile/settingsRoutes";
import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import PreferredLanguageToggle from "../../components/PreferredLanguageToggle";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useNotifications } from "../../hooks/useNotifications";
import { useQuizInstructions } from "../../hooks/useQuizInstructions";
import { useCreateQuizBatch } from "../../hooks/useCreateQuizBatch";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  buildStartQuizState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  navigateQuizBack,
  resolveQuizBackTarget,
} from "./quizFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import "./StartQuiz.css";
import "./StartQuizHeader.css";
import "./QuizInstruction.css";

const QuizInstruction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [batchCreateError, setBatchCreateError] = useState("");
  const persistedFlowState = useMemo(() => getStoredQuizFlowState(), []);
  const flowState = useMemo(
    () => buildQuizFlowState(location.state, persistedFlowState),
    [location.state, persistedFlowState]
  );
  const resolvedBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.instruction, buildQuizBackTarget(QUIZ_ROUTES.selection, flowState));
  useQuizBrowserBack(resolvedBackTarget);
  const { data: sidebarData } = useSidebarData();
  const { data: unreadNotificationsData } = useNotifications({ page: 1, unreadOnly: true });
  const quizData = flowState?.quizData || {};
  const selectedMode = flowState?.selectedMode || null;
  const profile = sidebarData?.user_data || {};
  const profileName = profile?.name || profile?.user_name || "Student";
  const profileImage =
    profile?.profile_image || profile?.image || profile?.user_image || profile?.avatar || "";
  const unreadNotificationCount = Number(unreadNotificationsData?.pagination?.totalRecords || 0);
  const unreadNotificationLabel =
    unreadNotificationCount > 99 ? "99+" : String(unreadNotificationCount);
  const quizTitle = quizData?.quizName || quizData?.title || "Quiz";
  const modeTitle = selectedMode?.title || "Quiz Mode";
  const selectedStateId = Number(flowState?.state_id || 0);
  const selectedCategoryId = Number(flowState?.category_id || 0);
  const {
    data: instructionData,
    isLoading: instructionsLoading,
    isFetching: instructionsFetching,
    isError: instructionsError,
  } = useQuizInstructions(
    {
      ...(selectedStateId > 0 ? { states_id: selectedStateId } : {}),
      category_id: selectedCategoryId,
    },
    {
      enabled: selectedCategoryId > 0,
    }
  );
  const browserLink = instructionData?.web_url || "https://qbank.vidyakasam.com/";
  const shouldShowQr = Number(instructionData?.is_qr_enabled || 0) === 1;
  const sanitizedInstructionsHtml = useMemo(
    () => DOMPurify.sanitize(instructionData?.description || ""),
    [instructionData?.description]
  );
  const instructionTitle = instructionData?.title || "Instructions";
  const isInstructionsPending = instructionsLoading || instructionsFetching;
  const createQuizBatchMutation = useCreateQuizBatch();
  const quizId = Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0);
  const quizMode = String(selectedMode?.dataKey || selectedMode?.id || "")
    .trim()
    .toUpperCase();
  const selectedLang = String(
    flowState?.selected_lang ||
      selectedMode?.langShortName ||
      quizData?.selected_lang ||
      "EN"
  )
    .trim()
    .toUpperCase() || "EN";

  useEffect(() => {
    persistQuizFlowState(flowState);
  }, [flowState]);

  const handleContinue = async () => {
    if (createQuizBatchMutation.isPending) {
      return;
    }

    if (!quizId || !quizMode) {
      setBatchCreateError("Quiz mode details are missing. Please select the quiz mode again.");
      return;
    }

    setBatchCreateError("");

    try {
      const response = await createQuizBatchMutation.mutateAsync({
        quiz_id: quizId,
        quiz_mode: quizMode,
        ...(selectedStateId > 0 ? { states_id: selectedStateId } : {}),
      });
      const assesmentBatchId = Number(response?.payload?.assesment_batch_id || 0);
      const instructionFlowState = buildQuizFlowState({
        ...flowState,
        selectedMode: {
          ...selectedMode,
          dataKey: quizMode,
          id: quizMode,
        },
        selected_lang: selectedLang,
        assesment_batch_id: assesmentBatchId,
        quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, buildStartQuizState(flowState)),
      });

      persistQuizFlowState(instructionFlowState);
      navigate(QUIZ_ROUTES.detail, {
        state: instructionFlowState,
      });
    } catch (error) {
      setBatchCreateError(error?.message || "Unable to start quiz right now.");
    }
  };

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
      `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0 start-quiz-shell">
                <div className="quiz-header-left instruction-shell__left">
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigateQuizBack(navigate, resolvedBackTarget)
                    }
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <div className="instruction-shell__titleWrap">
                    <div className="instruction-shell__eyebrow">Quiz setupp</div>
                    <h1 className="instruction-shell__title">{instructionTitle}</h1>
                  </div>
                </div>

                <div className="start-quiz-shell__actions">
                  <PreferredLanguageToggle profile={profile} />

                  <button
                    type="button"
                    className="start-quiz-shell__icon"
                    aria-label="Notifications"
                    onClick={() => navigate(SETTINGS_ROUTES.notifications)}
                  >
                    <i className="bi bi-bell" />
                    {unreadNotificationCount > 0 ? (
                      <span className="start-quiz-shell__badge">{unreadNotificationLabel}</span>
                    ) : null}
                  </button>

                  <div className="start-quiz-shell__profile" aria-hidden="true">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={profileName}
                        className="start-quiz-shell__profile-image"
                      />
                    ) : (
                      <span className="start-quiz-shell__profile-fallback">
                        {String(profileName).trim().charAt(0).toUpperCase() || "S"}
                      </span>
                    )}
                    <span className="start-quiz-shell__profile-name">Hi, {profileName}</span>
                    <i className="bi bi-chevron-down start-quiz-shell__profile-caret" />
                  </div>
                </div>
              </div>
            </div>

            <div className="instructionContent">
              <div className="instruction-summary">
                <div>
                  <div className="instruction-summary__eyebrow">{modeTitle}</div>
                  <h2 className="instruction-summary__title">{quizTitle}</h2>
                </div>
                {selectedMode?.description ? (
                  <p className="instruction-summary__description">{selectedMode.description}</p>
                ) : null}
              </div>

              <div className="qbl-browserCard mt-20">
                <div className="qbl-browserCard__copy">
                  <div className="qbl-browserCard__icon" aria-hidden="true">
                    <i className="bi bi-link" />
                  </div>
                  <div>
                    <p className="qbl-browserCard__title">Continue on browser..</p>
                    <p className="qbl-browserCard__link">{browserLink}</p>
                  </div>
                </div>

                <div className="qbl-browserCard__actions" aria-label="Quiz instruction tools">
                  <button type="button" className="qbl-browserCard__action" aria-label="Copy link">
                    <i className="bi bi-copy" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="qbl-browserCard__action qbl-browserCard__action--primary"
                    aria-label="Share link"
                  >
                    <i className="bi bi-share-fill" />
                  </button>
                  {shouldShowQr ? (
                    <button
                      type="button"
                      className="qbl-browserCard__action"
                      aria-label="Show QR code"
                    >
                      <i className="bi bi-qr-code-scan" />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="quiz-inst-text">
                {isInstructionsPending ? (
                  <div className="instruction-shimmer" aria-hidden="true">
                    {[...Array(6)].map((_, index) => (
                      <span
                        key={`instruction-shimmer-${index}`}
                        className={`dashboard-skeleton dashboard-skeleton--line instruction-shimmer__line${
                          index % 3 === 2 ? " instruction-shimmer__line--short" : ""
                        }`}
                      />
                    ))}
                  </div>
                ) : instructionsError ? (
                  <p>Unable to load instructions right now.</p>
                ) : sanitizedInstructionsHtml ? (
                  <div
                    className="instruction-html"
                    dangerouslySetInnerHTML={{ __html: sanitizedInstructionsHtml }}
                  />
                ) : (
                  <p>No instructions available.</p>
                )}
              </div>

              <div className="quizInstruction-Btn">
                {batchCreateError ? <p className="text-danger mb-2">{batchCreateError}</p> : null}
                <button
                  type="button"
                  className="start-quiz-btn"
                  onClick={handleContinue}
                  disabled={createQuizBatchMutation.isPending}
                >
                  <span className="continue-play-icon">
                    <i className={`bi ${createQuizBatchMutation.isPending ? "bi-hourglass-split" : "bi-play-fill"}`} />
                  </span>
                  <span>{createQuizBatchMutation.isPending ? "Starting..." : "Continue"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default QuizInstruction;
